import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { setMaintenanceMode, addNotification } from '../../store/slices/uiSlice';
import { dbProducts } from '../../data/db';
import { CATEGORY_TREE } from '../../data/categoryStructure';
import './AdminPanel.css';
import { DynamicIcon } from '../../components/common/SvgIcons';
import { productsApi, ordersApi, rfqsApi, settingsApi, authApi } from '../../api/apiClient';
import { fetchProducts, fetchCategories, logoutUser } from '../../store/thunks';
import { AppDispatch } from '../../store';
import { usePermission } from '../../hooks/usePermission';

const AdminPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const cms = useSelector((state: RootState) => state.cms);
  const isMaintenanceMode = useSelector((state: RootState) => state.ui.isMaintenanceMode);
  const { isSuperAdmin } = usePermission();

  
  const [activeTab, setActiveTab] = useState<'home' | 'shop_layout' | 'product_layout' | 'cart_layout' | 'navigation' | 'inventory' | 'settings' | 'customers' | 'orders' | 'rfqs' | 'how_to_add'>('home');
  const [activeHomeSubTab, setActiveHomeSubTab] = useState('banners');
  
  const [localMaintenance, setLocalMaintenance] = useState(isMaintenanceMode);

  // Load orders and customers from backend on mount

  const [adminOrders, setAdminOrders] = useState<any[]>([]);
  const [adminCustomers, setAdminCustomers] = useState<any[]>([]);
  const [suspendedUsers, setSuspendedUsers] = useState<string[]>([]);

  useEffect(() => {
    ordersApi.getAll().then((orders: any) => setAdminOrders(orders)).catch(() => {});
    authApi.getUsers().then((users: any) => setAdminCustomers(users)).catch(() => {});
  }, []);

  const [adminRFQs, setAdminRFQs] = useState<any[]>([]);

  useEffect(() => {
    rfqsApi.getAll().then((rfqs: any) => setAdminRFQs(rfqs)).catch(() => {});
  }, []);

  const handleRFQAction = (rfqId: string, actionType: 'approve' | 'negotiate') => {
    let updated;
    if (actionType === 'approve') {
      const approvedPrice = prompt('Enter Approved Wholesale Unit Price (₹):', '1450');
      if (approvedPrice === null) return;
      updated = adminRFQs.map(r => r.id === rfqId ? { ...r, status: `Discount Offered`, specialPrice: parseFloat(approvedPrice) } : r);
    } else {
      const remarks = prompt('Enter Negotiation Remarks / Counter Offer:', 'Kindly buy Qty 200+ for the target rate.');
      if (remarks === null) return;
      updated = adminRFQs.map(r => r.id === rfqId ? { ...r, status: `Negotiation Countered: ${remarks}` } : r);
    }
    setAdminRFQs(updated);
    dispatch(addNotification({ type: 'success', title: 'Success', message: 'Real-time Wholesale Quote updated for the buyer!' }));
  };

  const handleFulfillmentChange = (orderId: string, newStatus: string) => {
    const updated = adminOrders.map(o => (o.orderId || o.id) === orderId ? { ...o, status: newStatus } : o);
    setAdminOrders(updated);
    dispatch(addNotification({ type: 'success', title: 'Success', message: `Order "${orderId}" status updated to "${newStatus}" in real-time!` }));
  };

  const handleToggleSuspend = (email: string) => {
    if (suspendedUsers.includes(email)) {
      setSuspendedUsers(prev => prev.filter(e => e !== email));
      dispatch(addNotification({ type: 'success', title: 'Success', message: `Customer account "${email}" has been successfully Restored.` }));
    } else {
      setSuspendedUsers(prev => [...prev, email]);
      dispatch(addNotification({ type: 'warning', title: 'Suspended', message: `Customer account "${email}" has been Suspended in real-time.` }));
    }
  };

  // Dynamic Products State & Pagination
  const reduxProducts = useSelector((state: RootState) => state.products.products);
  const [productsList, setProductsList] = useState<any[]>(dbProducts.products);

  // Sync Redux products into local admin list when backend fetch completes
  useEffect(() => {
    if (reduxProducts.length > 0) {
      setProductsList(reduxProducts);
    }
  }, [reduxProducts]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Excel/CSV Importer States
  const [importMode, setImportMode] = useState<'standard' | 'stock_update'>('standard');
  const [conflictMode, setConflictMode] = useState<'all' | 'stock' | 'price' | 'skip'>('all');
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [importLogs, setImportLogs] = useState<string[]>([]);
  const [importSummary, setImportSummary] = useState<{added: number, updated: number, skipped: number, failed: number} | null>(null);

  // New Product Form States
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [newProdName, setNewProdName] = useState('');
  const [newProdBrand, setNewProdBrand] = useState('');
  const [newProdCategory, setNewProdCategory] = useState('Home Security');
  const [newProdCustomCategory, setNewProdCustomCategory] = useState('');
  const [newProdPrice, setNewProdPrice] = useState('');
  const [newProdOldPrice, setNewProdOldPrice] = useState('');
  const [newProdDiscount, setNewProdDiscount] = useState('');
  const [newProdDescription, setNewProdDescription] = useState('');
  const [newProdStock, setNewProdStock] = useState(10);
  const [newProdRating, setNewProdRating] = useState('4.5');
  const [newProdReviewsCount, setNewProdReviewsCount] = useState(10);
  const [newProdIsDeal, setNewProdIsDeal] = useState(false);
  const [newProdImageOption, setNewProdImageOption] = useState<'url' | 'local'>('url');
  const [newProdImageUrl, setNewProdImageUrl] = useState('');
  const [newProdImageFile, setNewProdImageFile] = useState<string | null>(null);

  const resetNewProductForm = () => {
    setNewProdName('');
    setNewProdBrand('');
    setNewProdCategory('Home Security');
    setNewProdCustomCategory('');
    setNewProdPrice('');
    setNewProdOldPrice('');
    setNewProdDiscount('');
    setNewProdDescription('');
    setNewProdStock(10);
    setNewProdRating('4.5');
    setNewProdReviewsCount(10);
    setNewProdIsDeal(false);
    setNewProdImageOption('url');
    setNewProdImageUrl('');
    setNewProdImageFile(null);
  };

  const handleLocalImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProdImageFile(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Download Template helpers
  const downloadProductTemplate = () => {
    const headers = [
      'Product Name', 'SKU', 'Category', 'Subcategory', 'Brand',
      'Description', 'Specifications', 'Features', 'Price', 'Discount',
      'Stock Quantity', 'Stock Status', 'Tags', 'Image URL 1', 'Image URL 2',
      'SEO Title', 'SEO Description'
    ];
    const sampleRow = [
      'Honeywell 4MP Dome Camera | HW-IPC-D4', 'HW-IPC-D4', 'Security Systems', 'IP Cameras', 'Honeywell',
      'High performance corporate dome camera.', 'Resolution: 4MP, Lens: 2.8mm, IR: 30m', 'Waterproof, PoE', '₹5,499', '25% OFF',
      '35', 'In Stock', 'Dome, IP, Honeywell', 'https://static.wixstatic.com/media/fa1b6a_8e307d54b61048719bee187a7ed84a35~mv2.jpg', '',
      'Honeywell 4MP Dome Camera HW-IPC-D4', 'High performance Honeywell 4MP Dome Camera'
    ];
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
      + [headers.join(','), sampleRow.map(v => `"${v.replace(/"/g, '""')}"`).join(',')].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "b2b_product_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadStockTemplate = () => {
    const headers = ['SKU', 'Product Name', 'Stock Quantity', 'Stock Status', 'Price'];
    const sampleRow = ['HW-IPC-D4', 'Honeywell 4MP Dome Camera | HW-IPC-D4', '42', 'In Stock', '₹5,200'];
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
      + [headers.join(','), sampleRow.map(v => `"${v.replace(/"/g, '""')}"`).join(',')].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "b2b_stock_update_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadSheetJS = (): Promise<any> => {
    return new Promise((resolve) => {
      if ((window as any).XLSX) {
        resolve((window as any).XLSX);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
      script.onload = () => resolve((window as any).XLSX);
      script.onerror = () => resolve(null);
      document.body.appendChild(script);
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportProgress(0);
    setImportLogs(["Reading uploaded file..."]);
    
    const reader = new FileReader();
    reader.onload = async (evt: any) => {
      try {
        const data = evt.target.result;
        let rows: any[] = [];
        
        if (file.name.endsWith('.csv')) {
          // Native CSV Parser
          const text = new TextDecoder().decode(data);
          rows = text.split('\n').map(line => {
            // Split by comma ignoring commas inside quotes
            const matches = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || line.split(',');
            return matches.map(m => m.replace(/^"|"$/g, '').trim());
          }).filter(r => r.length > 0 && r.some(c => c.trim() !== ''));
        } else {
          // Excel parser
          const XLSX = await loadSheetJS();
          if (!XLSX) {
            setImportLogs(prev => [...prev, "ERROR: SheetJS Excel library failed to load. Please convert sheet to CSV first."]);
            setImportProgress(null);
            return;
          }
          const arr = new Uint8Array(data);
          const workbook = XLSX.read(arr, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[];
        }

        if (rows.length < 2) {
          setImportLogs(prev => [...prev, "ERROR: File has no product rows to import."]);
          setImportProgress(null);
          return;
        }

        const headers = rows[0].map((h: any) => h.toString().trim());
        const rawDataRows = rows.slice(1);
        
        setImportLogs(prev => [...prev, `Loaded ${rawDataRows.length} data rows with headers: [${headers.join(', ')}]`]);

        // Simple validation checks on structure
        const headerMap = headers.reduce((acc: Record<string, number>, h: string, idx: number) => {
          acc[h.toLowerCase().replace(/[^a-z0-9]/g, '')] = idx;
          return acc;
        }, {});

        // Store headers map inside previews
        const mapped = rawDataRows.map((r, rowIdx) => {
          const getValue = (keys: string[]) => {
            for (const key of keys) {
              const cleanedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
              if (headerMap[cleanedKey] !== undefined) return r[headerMap[cleanedKey]];
            }
            return '';
          };

          const name = getValue(['Product Name', 'ProductName', 'Name']);
          const sku = getValue(['SKU', 'ModelNumber', 'sku', 'Model Number']);
          const category = getValue(['Category', 'category']);
          const subcategory = getValue(['Subcategory', 'subcategory']);
          const brand = getValue(['Brand', 'brand']);
          const price = getValue(['Price', 'price']);
          const stock = getValue(['Stock Quantity', 'StockQuantity', 'stock', 'quantity']);

          const errors: string[] = [];
          if (importMode === 'standard') {
            if (!name) errors.push("Missing Product Name");
            if (!category) errors.push("Missing Category");
            if (!price) errors.push("Missing Price");
          } else {
            if (!sku) errors.push("Missing SKU for inventory update");
            if (!stock) errors.push("Missing Stock Quantity");
          }

          // Check duplicate/existing
          const exists = productsList.some(p => 
            (sku && p.sku === sku) || 
            (name && p.name.toLowerCase() === name.toString().toLowerCase())
          );

          return {
            rowIdx: rowIdx + 2,
            name: name || 'Unknown Product',
            sku: sku || 'N/A',
            category: category || 'N/A',
            subcategory: subcategory || 'N/A',
            brand: brand || 'N/A',
            price: price || 'N/A',
            stock: stock || '0',
            errors,
            status: errors.length > 0 ? 'invalid' : (exists ? 'exists' : 'new'),
            raw: r,
            headerMap
          };
        });

        setImportPreview(mapped);
        setImportProgress(100);
        setImportLogs(prev => [...prev, "Validation preview complete!"]);
      } catch (err: any) {
        setImportLogs(prev => [...prev, `ERROR reading file: ${err.message}`]);
        setImportProgress(null);
      }
    };

    if (file.name.endsWith('.csv')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleExecuteImport = async () => {
    if (importPreview.length === 0) return;
    setImportProgress(0);
    setImportLogs(['Sending import data to backend server...']);

    // Map preview rows into clean product payloads
    const products = importPreview
      .filter(row => row.status !== 'invalid')
      .map(row => {
        const getValue = (keys: string[]) => {
          for (const key of keys) {
            const cleanedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (row.headerMap[cleanedKey] !== undefined) return row.raw[row.headerMap[cleanedKey]];
          }
          return '';
        };
        return {
          name: getValue(['Product Name', 'ProductName', 'Name']),
          sku: getValue(['SKU', 'ModelNumber', 'sku', 'Model Number']),
          category: getValue(['Category', 'category']),
          subcategory: getValue(['Subcategory', 'subcategory']),
          brand: getValue(['Brand', 'brand']),
          price: getValue(['Price', 'price']),
          oldPrice: getValue(['Old Price', 'OldPrice', 'oldPrice', 'MRP']),
          discount: getValue(['Discount', 'discount']),
          stock: getValue(['Stock Quantity', 'StockQuantity', 'stock', 'quantity']),
          stockStatus: getValue(['Stock Status', 'StockStatus', 'stockStatus', 'inStock']),
          description: getValue(['Description', 'description']),
          specifications: getValue(['Specifications', 'specifications']),
          features: getValue(['Features', 'features']),
          tags: getValue(['Tags', 'tags']),
          image: getValue(['Image URL 1', 'ImageURL1', 'image', 'Image URL']),
        };
      });

    setImportLogs(prev => [...prev, `Prepared ${products.length} rows for import. Sending to backend...`]);
    setImportProgress(50);

    try {
      let result: any;
      if (importMode === 'stock_update') {
        result = await productsApi.bulkStockUpdate(products, 'Admin', 'upload.xlsx');
        setImportSummary({ added: 0, updated: result.updated, skipped: 0, failed: result.failed });
        setImportLogs(prev => [...prev, `Stock Update complete: ${result.updated} updated, ${result.failed} failed.`]);
      } else {
        result = await productsApi.bulkImport(products, conflictMode, 'Admin', 'upload.xlsx');
        setImportSummary({ added: result.added, updated: result.updated, skipped: result.skipped, failed: result.failed });
        setImportLogs(prev => [...prev, `Import complete: Added ${result.added}, Updated ${result.updated}, Skipped ${result.skipped}, Failed ${result.failed}.`]);
      }

      // Refresh products from backend
      dispatch(fetchProducts({ limit: 1000 }));
      dispatch(fetchCategories());

      setImportProgress(null);
      setImportPreview([]);
      dispatch(addNotification({ type: 'success', title: 'Import Complete', message: `Import finished! Added: ${result.added || 0}, Updated: ${result.updated || 0}` }));
    } catch (err: any) {
      setImportLogs(prev => [...prev, `ERROR: ${err.message}`]);
      setImportProgress(null);
      dispatch(addNotification({ type: 'error', title: 'Import Failed', message: err.message }));
    }
  };

  // Segmented sub-tab navigation state for inventory management
  const [inventorySubTab, setInventorySubTab] = useState<'products' | 'categories' | 'bulk'>('products');

  // Category CRUD states
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatDescription, setNewCatDescription] = useState('');
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [editingCatDescription, setEditingCatDescription] = useState('');
  const [addingSubcatCatId, setAddingSubcatCatId] = useState<string | null>(null);
  const [newSubcatName, setNewSubcatName] = useState('');

  // Category CRUD methods
  const handleCreateCategory = () => {
    if (!newCatName.trim()) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Category Name is required!' }));
      return;
    }
    const catId = newCatName.toLowerCase().replace(/[^a-z0-9]/g, '-');
    const newCat = {
      id: catId,
      name: newCatName,
      slug: catId,
      path: `/shop/${catId}`,
      icon: 'folder',
      description: newCatDescription || `${newCatName} solutions.`,
      productCount: 0,
      children: []
    };

    CATEGORY_TREE.push(newCat);
    
    const saved = localStorage.getItem('categories_db_custom');
    const customList = saved ? JSON.parse(saved) : [];
    customList.push(newCat);
    localStorage.setItem('categories_db_custom', JSON.stringify(customList));

    dispatch(addNotification({ type: 'success', title: 'Success', message: `Category "${newCatName}" created successfully!` }));
    setIsAddingCategory(false);
    setNewCatName('');
    setNewCatDescription('');
  };

  const handleEditCategorySave = () => {
    if (!editingCatName.trim()) return;
    
    const idx = CATEGORY_TREE.findIndex(c => c.id === editingCatId);
    if (idx !== -1) {
      CATEGORY_TREE[idx].name = editingCatName;
      CATEGORY_TREE[idx].description = editingCatDescription;
      
      const edited = JSON.parse(localStorage.getItem('categories_db_edited') || '[]');
      const existingIdx = edited.findIndex((c: any) => c.id === editingCatId);
      if (existingIdx !== -1) {
        edited[existingIdx] = { id: editingCatId, name: editingCatName, description: editingCatDescription };
      } else {
        edited.push({ id: editingCatId, name: editingCatName, description: editingCatDescription });
      }
      localStorage.setItem('categories_db_edited', JSON.stringify(edited));
    }
    
    dispatch(addNotification({ type: 'success', title: 'Success', message: 'Category updated successfully!' }));
    setEditingCatId(null);
  };

  const handleDeleteCategory = (catId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete category "${name}" and all its subcategories?`)) return;
    
    const activeCats = CATEGORY_TREE.filter(c => c.id !== catId);
    CATEGORY_TREE.length = 0;
    CATEGORY_TREE.push(...activeCats);

    const deleted = JSON.parse(localStorage.getItem('categories_db_deleted_ids') || '[]');
    deleted.push(catId);
    localStorage.setItem('categories_db_deleted_ids', JSON.stringify(deleted));

    dispatch(addNotification({ type: 'success', title: 'Deleted', message: `Category "${name}" has been deleted.` }));
  };

  const handleAddSubcategory = (catId: string) => {
    if (!newSubcatName.trim()) return;
    
    const cat = CATEGORY_TREE.find(c => c.id === catId);
    if (cat) {
      const subId = newSubcatName.toLowerCase().replace(/[^a-z0-9]/g, '-');
      const newSub = {
        id: subId,
        name: newSubcatName,
        slug: subId,
        path: `/shop/${cat.slug}/${subId}`,
        icon: 'folder',
        productCount: 0
      };
      if (!cat.children) cat.children = [];
      cat.children.push(newSub);

      const saved = localStorage.getItem('categories_db_custom');
      const customList = saved ? JSON.parse(saved) : [];
      const customCatIdx = customList.findIndex((c: any) => c.id === catId);
      if (customCatIdx !== -1) {
        if (!customList[customCatIdx].children) customList[customCatIdx].children = [];
        customList[customCatIdx].children.push(newSub);
      } else {
        customList.push(cat);
      }
      localStorage.setItem('categories_db_custom', JSON.stringify(customList));
      
      dispatch(addNotification({ type: 'success', title: 'Success', message: `Subcategory "${newSubcatName}" added!` }));
      setAddingSubcatCatId(null);
      setNewSubcatName('');
    }
  };

  const handleDeleteSubcategory = (catId: string, subId: string, subName: string) => {
    if (!window.confirm(`Are you sure you want to delete subcategory "${subName}"?`)) return;
    
    const cat = CATEGORY_TREE.find(c => c.id === catId);
    if (cat && cat.children) {
      cat.children = cat.children.filter(s => s.id !== subId);
      
      const deleted = JSON.parse(localStorage.getItem('categories_db_deleted_ids') || '[]');
      deleted.push(`${catId}/${subId}`);
      deleted.push(subId);
      localStorage.setItem('categories_db_deleted_ids', JSON.stringify(deleted));
      
      const saved = localStorage.getItem('categories_db_custom');
      const customList = saved ? JSON.parse(saved) : [];
      const customCatIdx = customList.findIndex((c: any) => c.id === catId);
      if (customCatIdx !== -1 && customList[customCatIdx].children) {
        customList[customCatIdx].children = customList[customCatIdx].children.filter((s: any) => s.id !== subId);
        localStorage.setItem('categories_db_custom', JSON.stringify(customList));
      }
    }
    dispatch(addNotification({ type: 'success', title: 'Deleted', message: `Subcategory "${subName}" deleted.` }));
  };

  const handleProductDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      await productsApi.delete(id);
      setProductsList(prev => prev.filter(p => p.id !== id));
      dispatch(fetchProducts({ limit: 1000 }));
      dispatch(addNotification({ type: 'success', title: 'Deleted', message: `Product "${name}" deleted successfully.` }));
    } catch (err: any) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: `Failed to delete product: ${err.message}` }));
    }
  };

  const handleSaveProduct = async (id: string, name: string) => {
    const p = productsList.find(item => item.id === id);
    if (!p) return;

    try {
      const payload = {
        name: p.name,
        brand: p.brand,
        price: String(p.price).startsWith('₹') ? String(p.price) : `₹${p.price}`,
        stockQuantity: p.stockQuantity,
        inStock: p.stockQuantity > 0,
        image: p.image,
        description: p.description
      };
      await productsApi.update(id, payload as any);
      dispatch(fetchProducts({ limit: 1000 }));
      dispatch(addNotification({ type: 'success', title: 'Success', message: `Product "${name}" saved successfully!` }));
    } catch (err: any) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: `Failed to save product: ${err.message}` }));
    }
  };


  const handleAddProductSubmit = async () => {
    if (!newProdName.trim()) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Product Name is required!' }));
      return;
    }
    if (!newProdBrand.trim()) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Brand is required!' }));
      return;
    }
    if (!newProdPrice.trim()) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Price is required!' }));
      return;
    }

    const finalCategory = newProdCategory === 'Custom' ? newProdCustomCategory : newProdCategory;
    if (!finalCategory.trim()) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Category is required!' }));
      return;
    }

    const finalImage = newProdImageOption === 'url' ? newProdImageUrl : newProdImageFile;
    if (!finalImage) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Product image is required!' }));
      return;
    }

    const payload = {
      name: newProdName,
      brand: newProdBrand,
      category: finalCategory,
      image: finalImage as string,
      price: newProdPrice.startsWith('₹') ? newProdPrice : `₹${newProdPrice}`,
      oldPrice: newProdOldPrice || newProdPrice,
      discount: newProdDiscount || '10% OFF',
      rating: newProdRating || '5.0',
      reviewsCount: newProdReviewsCount,
      inStock: true,
      stockQuantity: newProdStock,
      isDeal: newProdIsDeal,
      description: newProdDescription || `High quality ${newProdName} designed for industrial and home use.`
    };

    try {
      let createdProduct: any = null;
      try {
        createdProduct = await productsApi.create(payload as any);
      } catch (e) {
        console.warn('Backend API create failed, using dual fallback persistence:', e);
      }

      const newProductObj = createdProduct || {
        id: `prod_custom_${Date.now()}`,
        ...payload,
        slug: newProdName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      };

      // 1. Update state & Redux
      setProductsList(prev => [newProductObj, ...prev]);
      dispatch(fetchProducts({ limit: 1000 }));

      // 2. Dual-persist to static dbProducts in memory
      const existingIdx = dbProducts.products.findIndex((p: any) => p.id === newProductObj.id);
      if (existingIdx === -1) {
        dbProducts.products.unshift(newProductObj);
      } else {
        dbProducts.products[existingIdx] = newProductObj;
      }

      // 3. Dual-persist to browser localStorage fallback
      const savedCustom = localStorage.getItem('products_db_custom');
      const customList = savedCustom ? JSON.parse(savedCustom) : [];
      const customIdx = customList.findIndex((p: any) => p.id === newProductObj.id);
      if (customIdx === -1) {
        customList.unshift(newProductObj);
      } else {
        customList[customIdx] = newProductObj;
      }
      localStorage.setItem('products_db_custom', JSON.stringify(customList));

      dispatch(addNotification({ type: 'success', title: 'Success', message: `Product "${newProdName}" added successfully to Inventory!` }));
      setIsAddingProduct(false);
      resetNewProductForm();
    } catch (err: any) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: `Failed to create product: ${err.message}` }));
    }
  };



  const handleHomeUpdate = (section: any, data: any) => {
    dispatch(updateHomeSection({ section, data }));
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/admin/login');
  };

  const handleSaveSettings = async () => {
    try {
      await settingsApi.set('isMaintenanceMode', String(localMaintenance));
      dispatch(setMaintenanceMode(localMaintenance));
      dispatch(addNotification({ type: 'success', title: 'Success', message: 'Settings saved successfully!' }));
    } catch (err: any) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: `Failed to save settings: ${err.message}` }));
    }
  };

  const renderLayoutManager = (title: string, sections: any[]) => (
    <div className="admin-editor">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3>{title}</h3>
          <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>Drag and drop to reorder sections. Toggle visibility to hide sections from the storefront.</p>
        </div>
        <button className="admin-btn-save">Save Layout</button>
      </div>
      
      <div className="admin-sections-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {sections.map((section, index) => (
          <div key={section.id} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            background: '#fff', 
            padding: '15px 20px', 
            border: '1px solid #ddd', 
            borderRadius: '6px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ cursor: 'grab', marginRight: '15px', color: '#999' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="8" x2="20" y2="8" /><line x1="4" y1="16" x2="20" y2="16" />
              </svg>
            </div>
            <div style={{ width: '40px', fontWeight: 'bold', color: '#666' }}>
              {index + 1}.
            </div>
            <div style={{ flex: '1' }}>
              <div style={{ fontWeight: '600', color: '#333', fontSize: '15px' }}>{section.name}</div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Type: {section.type}</div>
            </div>
            
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '14px', color: '#555' }}>
                <input type="checkbox" defaultChecked={section.visible} disabled={section.locked} style={{ width: '16px', height: '16px' }} />
                Visible
              </label>
              <button className="admin-btn-save" style={{ background: '#f0f0f0', color: '#333', border: '1px solid #ddd', padding: '6px 12px' }}>
                Edit Content
              </button>
            </div>
          </div>
        ))}
      </div>
      <button className="admin-btn-add" style={{ marginTop: '20px' }}>+ Add New Custom Section</button>
    </div>
  );

  const renderHomeEditor = () => {
    switch (activeHomeSubTab) {
      case 'banners':
        return (
          <div className="admin-editor">
            <h3>Banner Slides</h3>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Subtitle</th>
                    <th>Image URL</th>
                    <th>Link</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cms.home.bannerSlides.map((slide, index) => (
                    <tr key={index}>
                      <td><input type="text" value={slide.title} onChange={(e) => {
                        const newSlides = [...cms.home.bannerSlides];
                        newSlides[index] = { ...slide, title: e.target.value };
                        handleHomeUpdate('bannerSlides', newSlides);
                      }} /></td>
                      <td><input type="text" value={slide.subtitle} onChange={(e) => {
                        const newSlides = [...cms.home.bannerSlides];
                        newSlides[index] = { ...slide, subtitle: e.target.value };
                        handleHomeUpdate('bannerSlides', newSlides);
                      }} /></td>
                      <td><input type="text" value={slide.image} onChange={(e) => {
                        const newSlides = [...cms.home.bannerSlides];
                        newSlides[index] = { ...slide, image: e.target.value };
                        handleHomeUpdate('bannerSlides', newSlides);
                      }} /></td>
                      <td><input type="text" value={slide.link} onChange={(e) => {
                        const newSlides = [...cms.home.bannerSlides];
                        newSlides[index] = { ...slide, link: e.target.value };
                        handleHomeUpdate('bannerSlides', newSlides);
                      }} /></td>
                      <td><button className="admin-btn-delete">Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="admin-btn-add">Add New Slide</button>
          </div>
        );
      case 'categories':
        return (
          <div className="admin-editor">
            <h3>Top Categories</h3>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Image URL</th>
                    <th>Link</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cms.home.categoriesList.map((cat, index) => (
                    <tr key={index}>
                      <td><input type="text" value={cat.name} onChange={(e) => {
                        const newCats = [...cms.home.categoriesList];
                        newCats[index] = { ...cat, name: e.target.value };
                        handleHomeUpdate('categoriesList', newCats);
                      }} /></td>
                      <td><input type="text" value={cat.image} onChange={(e) => {
                        const newCats = [...cms.home.categoriesList];
                        newCats[index] = { ...cat, image: e.target.value };
                        handleHomeUpdate('categoriesList', newCats);
                      }} /></td>
                      <td><input type="text" value={cat.link} onChange={(e) => {
                        const newCats = [...cms.home.categoriesList];
                        newCats[index] = { ...cat, link: e.target.value };
                        handleHomeUpdate('categoriesList', newCats);
                      }} /></td>
                      <td><button className="admin-btn-delete">Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'sections':
        return renderLayoutManager('Home Page Layout Manager', [
          { id: 'sec-hero', name: 'Hero Carousel & Banners', type: 'Carousel', visible: true, locked: true },
          { id: 'sec-features', name: '4 Services (Fastest Delivery, etc.)', type: 'Feature Strip', visible: true, locked: false },
          { id: 'sec-journey', name: 'Hi User, Continue Your Journey', type: 'Product Grid', visible: true, locked: false },
          { id: 'sec-viewed', name: 'Related To Items You Have Viewed', type: 'Category & Product Slider', visible: true, locked: false },
          { id: 'sec-recent', name: 'Recent View', type: 'Product Slider', visible: true, locked: false },
          { id: 'sec-best', name: 'Best Seller', type: 'Product Slider', visible: true, locked: false },
          { id: 'sec-security', name: cms.home.homeSecuritySection.title, type: 'Category Block', visible: true, locked: false },
          { id: 'sec-arch', name: cms.home.architectureSection.title, type: 'Category Block', visible: true, locked: false },
          { id: 'sec-elec', name: cms.home.electricalSection.title, type: 'Category Block', visible: true, locked: false },
          { id: 'sec-appliance', name: cms.home.appliancesSection.title, type: 'Category Block', visible: true, locked: false },
          { id: 'sec-about', name: 'About Us', type: 'Text Block', visible: true, locked: false },
        ]);
      default:
        return <div>Select a sub-tab to edit.</div>;
    }
  };

  return (
    <div className="admin-panel">
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <h2>Admin Panel</h2>
        </div>
        <nav className="admin-nav" style={{ overflowY: 'auto' }}>
          <div className="admin-nav-section-title" style={{ padding: '15px 20px 5px', fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>Storefront Layout</div>
          <button className={activeTab === 'home' ? 'active' : ''} onClick={() => setActiveTab('home')}>Home Page Layout</button>
          <button className={activeTab === 'shop_layout' ? 'active' : ''} onClick={() => setActiveTab('shop_layout')}>Shop Grid Layout</button>
          <button className={activeTab === 'product_layout' ? 'active' : ''} onClick={() => setActiveTab('product_layout')}>Product Detail Layout</button>
          <button className={activeTab === 'cart_layout' ? 'active' : ''} onClick={() => setActiveTab('cart_layout')}>Cart & Checkout Layout</button>
          <button className={activeTab === 'navigation' ? 'active' : ''} onClick={() => setActiveTab('navigation')}>Navigation & Footer</button>
          
          <div className="admin-nav-section-title" style={{ padding: '15px 20px 5px', fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>Management</div>
          <button className={activeTab === 'inventory' ? 'active' : ''} onClick={() => setActiveTab('inventory')}>Inventory & Products</button>
          <button className={activeTab === 'orders' ? 'active' : ''} onClick={() => setActiveTab('orders')}>Orders & Payments</button>
          <button className={activeTab === 'rfqs' ? 'active' : ''} onClick={() => setActiveTab('rfqs')}>B2B RFQ Submissions</button>
          <button className={activeTab === 'customers' ? 'active' : ''} onClick={() => setActiveTab('customers')}>Customers</button>
          <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>Global Settings</button>

          <div className="admin-nav-section-title" style={{ padding: '15px 20px 5px', fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>Help</div>
          <button className={activeTab === 'how_to_add' ? 'active' : ''} onClick={() => setActiveTab('how_to_add')} style={activeTab === 'how_to_add' ? {} : { color: '#10b981' }}><span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><DynamicIcon name="bookOpen" size={14} /> How to Add Products</span></button>
        </nav>
        <div className="admin-logout">
          <button onClick={handleLogout}>Logout</button>
        </div>
      </aside>

      <main className="admin-content">
        <header className="admin-header">
          <div className="admin-breadcrumb">
            Dashboard / {activeTab.toUpperCase()}
          </div>
          <div className="admin-user">
            <span>Welcome, Administrator</span>
          </div>
        </header>

        <section className="admin-main">
          {activeTab === 'home' && (
            <>
              <div className="admin-tabs-secondary">
                <button className={activeHomeSubTab === 'banners' ? 'active' : ''} onClick={() => setActiveHomeSubTab('banners')}>Banners</button>
                <button className={activeHomeSubTab === 'categories' ? 'active' : ''} onClick={() => setActiveHomeSubTab('categories')}>Categories</button>
                <button className={activeHomeSubTab === 'sections' ? 'active' : ''} onClick={() => setActiveHomeSubTab('sections')}>Page Sections</button>
              </div>
              {renderHomeEditor()}
            </>
          )}

          {activeTab === 'shop_layout' && renderLayoutManager('Shop Grid Layout Manager', [
            { id: 'shop-breadcrumb', name: 'Breadcrumb Navigation', type: 'Navigation Element', visible: true, locked: true },
            { id: 'shop-sidebar', name: 'Filter Sidebar', type: 'Interactive Filter', visible: true, locked: false },
            { id: 'shop-hero', name: 'Category Hero Banner', type: 'Static Banner', visible: true, locked: false },
            { id: 'shop-feat-cats', name: 'Featured Categories Strip', type: 'Category Cards', visible: true, locked: false },
            { id: 'shop-feat-prods', name: 'Featured Products Strip', type: 'Product Grid', visible: true, locked: false },
            { id: 'shop-main-grid', name: 'Main Product Listing Grid', type: 'Paginated Grid', visible: true, locked: true },
          ])}

          {activeTab === 'product_layout' && renderLayoutManager('Product Detail Layout Manager', [
            { id: 'pd-breadcrumb', name: 'Breadcrumb Navigation', type: 'Navigation Element', visible: true, locked: true },
            { id: 'pd-gallery', name: 'Product Image Gallery', type: 'Media Viewer', visible: true, locked: true },
            { id: 'pd-info', name: 'Product Info & Pricing', type: 'Data Block', visible: true, locked: true },
            { id: 'pd-bulk', name: 'Buy More & Save More Tiers', type: 'Promotional Table', visible: true, locked: false },
            { id: 'pd-actions', name: 'Cart & Wishlist Actions', type: 'Interactive Buttons', visible: true, locked: true },
            { id: 'pd-badges', name: 'Trust & B2B Badges', type: 'Static Badges', visible: true, locked: false },
            { id: 'pd-tabs', name: 'Details Tabs (Features, Specs, etc.)', type: 'Tabbed Interface', visible: true, locked: false },
            { id: 'pd-similar', name: 'Similar Products Slider', type: 'Product Slider', visible: true, locked: false },
          ])}

          {activeTab === 'cart_layout' && renderLayoutManager('Cart & Checkout Layout Manager', [
            { id: 'cart-items', name: 'Cart Items List', type: 'Dynamic List', visible: true, locked: true },
            { id: 'cart-summary', name: 'Order Summary (Subtotal, Tax, Shipping)', type: 'Calculation Block', visible: true, locked: true },
            { id: 'chk-billing', name: 'Billing & Delivery Form', type: 'Input Form', visible: true, locked: true },
            { id: 'chk-payment', name: 'Payment Options (Pay Now)', type: 'Interactive Actions', visible: true, locked: true },
            { id: 'chk-trust', name: 'Checkout Trust Indicators', type: 'Static Text', visible: true, locked: false },
          ])}

          {activeTab === 'navigation' && (
            <div className="admin-editor">
              <h3>Navigation Config</h3>
              <div className="admin-form-group">
                <label>Logo URL</label>
                <input type="text" value={cms.navigation.logo} onChange={(e) => dispatch(updateNavigation({ logo: e.target.value }))} />
              </div>
              <div className="admin-form-group">
                <label>Contact Number</label>
                <input type="text" value={cms.navigation.contactNumber} onChange={(e) => dispatch(updateNavigation({ contactNumber: e.target.value }))} />
              </div>
              <div className="admin-form-group">
                <label>Care Email</label>
                <input type="text" value={cms.navigation.email} onChange={(e) => dispatch(updateNavigation({ email: e.target.value }))} />
              </div>
            </div>
          )}

          {activeTab === 'inventory' && (() => {
             // Handle search & pagination calculations
             const filtered = productsList.filter(p => 
               p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
               p.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
               p.category.toLowerCase().includes(searchQuery.toLowerCase())
             );
             const totalPages = Math.ceil(filtered.length / itemsPerPage);
             const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

             const handleProductUpdate = (id: string, field: string, value: any) => {
               setProductsList(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
             };

             return (
               <div className="admin-editor">
                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                   <div>
                     <h3>Catalog &amp; Inventory Management</h3>
                     <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>Manage products, categories, subcategories, and bulk inventory uploads.</p>
                   </div>
                 </div>

                 {/* Segmented Sub Tab Selector */}
                 <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '20px', gap: '24px' }}>
                   <button 
                     onClick={() => setInventorySubTab('products')}
                     style={{ padding: '10px 4px', border: 'none', background: 'none', borderBottom: inventorySubTab === 'products' ? '3px solid #10b981' : '3px solid transparent', color: inventorySubTab === 'products' ? '#10b981' : '#64748b', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                   >
                     <DynamicIcon name="box" size={14} /> Storefront Products
                   </button>
                   <button 
                     onClick={() => setInventorySubTab('categories')}
                     style={{ padding: '10px 4px', border: 'none', background: 'none', borderBottom: inventorySubTab === 'categories' ? '3px solid #10b981' : '3px solid transparent', color: inventorySubTab === 'categories' ? '#10b981' : '#64748b', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                   >
                     <DynamicIcon name="folder" size={14} /> Category &amp; Subcategories
                   </button>
                   <button 
                     onClick={() => setInventorySubTab('bulk')}
                     style={{ padding: '10px 4px', border: 'none', background: 'none', borderBottom: inventorySubTab === 'bulk' ? '3px solid #10b981' : '3px solid transparent', color: inventorySubTab === 'bulk' ? '#10b981' : '#64748b', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                   >
                     <DynamicIcon name="fileText" size={14} /> Bulk Importer
                   </button>
                 </div>

                 {/* Tab 1: Products List Manager */}
                 {inventorySubTab === 'products' && (
                   <>
                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px' }}>
                       <button 
                         onClick={() => setIsAddingProduct(!isAddingProduct)}
                         style={{
                           background: isAddingProduct ? '#ef4444' : '#10b981',
                           color: '#fff',
                           border: 'none',
                           padding: '10px 16px',
                           borderRadius: '6px',
                           cursor: 'pointer',
                           fontWeight: '600',
                           fontSize: '14px',
                           transition: 'background 0.2s',
                           display: 'inline-flex',
                           alignItems: 'center',
                           gap: '6px'
                         }}
                       >
                         <DynamicIcon name="plus" size={14} />
                         {isAddingProduct ? 'Cancel & Close' : 'Add New Product'}
                       </button>
                       <input 
                         type="text" 
                         placeholder="Search products by name, brand, or category..." 
                         value={searchQuery}
                         onChange={(e) => {
                           setSearchQuery(e.target.value);
                           setCurrentPage(1);
                         }}
                         style={{
                           padding: '8px 14px',
                           border: '1px solid #ccc',
                           borderRadius: '6px',
                           width: '320px',
                           outline: 'none',
                           fontSize: '14px'
                         }}
                       />
                     </div>

                     {isAddingProduct && (
                       <div className="admin-add-product-card" style={{
                         background: '#fff',
                         border: '1.5px solid #e2e8f0',
                         borderRadius: '12px',
                         padding: '24px',
                         marginBottom: '24px',
                         boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
                       }}>
                         <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Add New Storefront Product</h4>
                         
                         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Product Name *</label>
                             <input 
                               type="text" 
                               placeholder="e.g. Hikvision Smart Biometric Scanner" 
                               value={newProdName} 
                               onChange={(e) => setNewProdName(e.target.value)} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                             />
                           </div>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Brand *</label>
                             <input 
                               type="text" 
                               placeholder="e.g. Hikvision" 
                               value={newProdBrand} 
                               onChange={(e) => setNewProdBrand(e.target.value)} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                             />
                           </div>
                         </div>

                         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Category *</label>
                             <select 
                               value={newProdCategory}
                               onChange={(e) => setNewProdCategory(e.target.value)}
                               style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#fff' }}
                             >
                               {CATEGORY_TREE.map(c => (
                                 <option key={c.id} value={c.name}>{c.name}</option>
                               ))}
                               <option value="Custom">Custom / Add New...</option>
                             </select>
                             {newProdCategory === 'Custom' && (
                               <input 
                                 type="text" 
                                 placeholder="Enter custom category name"
                                 value={newProdCustomCategory}
                                 onChange={(e) => setNewProdCustomCategory(e.target.value)}
                                 style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', marginTop: '8px' }}
                               />
                             )}
                           </div>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Price (₹) *</label>
                             <input 
                               type="text" 
                               placeholder="e.g. ₹4,500" 
                               value={newProdPrice} 
                               onChange={(e) => setNewProdPrice(e.target.value)} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                             />
                           </div>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Stock Quantity</label>
                             <input 
                               type="number" 
                               value={newProdStock} 
                               onChange={(e) => setNewProdStock(parseInt(e.target.value) || 0)} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                             />
                           </div>
                         </div>

                         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Old/MRP Price (Optional)</label>
                             <input 
                               type="text" 
                               placeholder="e.g. ₹6,000" 
                               value={newProdOldPrice} 
                               onChange={(e) => setNewProdOldPrice(e.target.value)} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                             />
                           </div>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Discount Label (Optional)</label>
                             <input 
                               type="text" 
                               placeholder="e.g. 25% OFF" 
                               value={newProdDiscount} 
                               onChange={(e) => setNewProdDiscount(e.target.value)} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                             />
                           </div>
                         </div>

                         <div style={{ marginBottom: '16px' }}>
                           <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Description</label>
                           <textarea 
                             rows={3}
                             placeholder="Write details about features, warranty, and contents..."
                             value={newProdDescription}
                             onChange={(e) => setNewProdDescription(e.target.value)}
                             style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', minHeight: '80px' }}
                           />
                         </div>

                         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Image URL</label>
                             <input 
                               type="text" 
                               placeholder="https://example.com/image.jpg" 
                               value={newProdImageUrl} 
                               onChange={(e) => setNewProdImageUrl(e.target.value)} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                             />
                           </div>
                           <div>
                             <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Or Upload Local Image</label>
                             <input 
                               type="file" 
                               accept="image/*" 
                               onChange={handleLocalImageChange} 
                               style={{ width: '100%', padding: '8px', fontSize: '13px' }}
                             />
                           </div>
                         </div>

                         <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                           <button 
                             onClick={() => { setIsAddingProduct(false); resetNewProductForm(); }} 
                             style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                           >
                             Cancel
                           </button>
                           <button 
                             onClick={handleAddProductSubmit} 
                             style={{ background: '#10b981', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                           >
                             Add Product to Inventory
                           </button>
                         </div>
                       </div>
                     )}

                     <div className="admin-table-container">
                       <table className="admin-table">
                         <thead>
                            <tr>
                              <th>Product ID</th>
                              <th>Product Image</th>
                              <th>Product Name</th>
                              <th>Brand</th>
                              <th>Category</th>
                              <th>Price</th>
                              <th>Stock Qty</th>
                              <th>Actions</th>
                            </tr>
                         </thead>
                         <tbody>
                            {paginated.map(p => (
                              <tr key={p.id}>
                                <td style={{ fontWeight: '600', color: '#666' }}>{p.id}</td>
                                <td>
                                  <img 
                                    src={p.image} 
                                    alt={p.name} 
                                    style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '4px', background: '#f9f9f9', border: '1px solid #eee' }} 
                                  />
                                </td>
                                <td>
                                  <input 
                                    type="text" 
                                    value={p.name} 
                                    onChange={(e) => handleProductUpdate(p.id, 'name', e.target.value)} 
                                    style={{ width: '280px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                                  />
                                </td>
                                <td>
                                  <input 
                                    type="text" 
                                    value={p.brand} 
                                    onChange={(e) => handleProductUpdate(p.id, 'brand', e.target.value)} 
                                    style={{ width: '100px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                                  />
                                </td>
                                <td style={{ color: '#555', fontSize: '13px' }}>{p.category}</td>
                                <td>
                                  <input 
                                    type="text" 
                                    value={p.price} 
                                    onChange={(e) => handleProductUpdate(p.id, 'price', e.target.value)} 
                                    style={{ width: '90px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                                  />
                                </td>
                                <td>
                                  <input 
                                    type="number" 
                                    value={p.stockQuantity} 
                                    onChange={(e) => handleProductUpdate(p.id, 'stockQuantity', parseInt(e.target.value) || 0)} 
                                    style={{ width: '70px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                                  />
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    <button 
                                      className="admin-btn-save"
                                      onClick={() => handleSaveProduct(p.id, p.name)}
                                      style={{ background: '#10b981', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                    >
                                      Save
                                    </button>
                                    {isSuperAdmin && (
                                      <button 
                                        className="admin-btn-save"
                                        onClick={() => handleProductDelete(p.id, p.name)}
                                        style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                         </tbody>
                       </table>
                     </div>

                     {totalPages > 1 && (
                       <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                         <button 
                           disabled={currentPage === 1}
                           onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                           className="admin-btn-save"
                           style={{ background: currentPage === 1 ? '#f0f0f0' : '#4ABCE8', color: currentPage === 1 ? '#999' : '#fff', border: 'none', padding: '6px 12px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                         >
                           Previous
                         </button>
                         <span style={{ fontSize: '14px', color: '#555' }}>
                           Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({filtered.length} products found)
                         </span>
                         <button 
                           disabled={currentPage === totalPages}
                           onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                           className="admin-btn-save"
                           style={{ background: currentPage === totalPages ? '#f0f0f0' : '#4ABCE8', color: currentPage === totalPages ? '#999' : '#fff', border: 'none', padding: '6px 12px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                         >
                           Next
                         </button>
                       </div>
                     )}
                   </>
                 )}

                 {/* Tab 2: Category Structure Manager */}
                 {inventorySubTab === 'categories' && (
                   <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                       <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Manage Category Structures</h4>
                       <button 
                         onClick={() => setIsAddingCategory(!isAddingCategory)}
                         style={{ background: '#10b981', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                       >
                         <DynamicIcon name="plus" size={12} />
                         {isAddingCategory ? 'Cancel' : 'Create Category'}
                       </button>
                     </div>

                     {isAddingCategory && (
                       <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                         <h5 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>Create New Category</h5>
                         <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                           <input 
                             type="text" 
                             placeholder="Category Name" 
                             value={newCatName} 
                             onChange={(e) => setNewCatName(e.target.value)} 
                             style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                           />
                           <textarea 
                             placeholder="Description" 
                             value={newCatDescription} 
                             onChange={(e) => setNewCatDescription(e.target.value)} 
                             style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', minHeight: '60px' }}
                           />
                           <button 
                             onClick={handleCreateCategory} 
                             style={{ alignSelf: 'flex-end', background: '#10b981', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                           >
                             Create
                           </button>
                         </div>
                       </div>
                     )}

                     {editingCatId && (
                       <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                         <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#b45309' }}>Edit Category Details</h5>
                         <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                           <input 
                             type="text" 
                             placeholder="Category Name" 
                             value={editingCatName} 
                             onChange={(e) => setEditingCatName(e.target.value)} 
                             style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                           />
                           <textarea 
                             placeholder="Description" 
                             value={editingCatDescription} 
                             onChange={(e) => setEditingCatDescription(e.target.value)} 
                             style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', minHeight: '60px' }}
                           />
                           <div style={{ display: 'flex', gap: '8px', alignSelf: 'flex-end' }}>
                             <button onClick={() => setEditingCatId(null)} style={{ background: '#cbd5e1', color: '#334155', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
                             <button onClick={handleEditCategorySave} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Save Changes</button>
                           </div>
                         </div>
                       </div>
                     )}

                     <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                       {CATEGORY_TREE.map(cat => (
                         <div key={cat.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', background: '#f8fafc' }}>
                           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                             <div>
                               <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{cat.name}</h5>
                               <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>{cat.description || 'No description provided.'}</p>
                             </div>
                             <div style={{ display: 'flex', gap: '8px' }}>
                               <button 
                                 onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); setEditingCatDescription(cat.description || ''); }}
                                 style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}
                                >
                                 Edit
                               </button>
                               {isSuperAdmin && (
                                 <button 
                                   onClick={() => handleDeleteCategory(cat.id, cat.name)}
                                   style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}
                                 >
                                   Delete
                                 </button>
                               )}
                             </div>
                           </div>

                           <div style={{ marginTop: '12px', paddingLeft: '12px', borderLeft: '2px solid #e2e8f0' }}>
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                               <span style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Subcategories:</span>
                               <button 
                                 onClick={() => setAddingSubcatCatId(addingSubcatCatId === cat.id ? null : cat.id)}
                                 style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}
                               >
                                 {addingSubcatCatId === cat.id ? 'Cancel' : '+ Add Subcategory'}
                               </button>
                             </div>

                             {addingSubcatCatId === cat.id && (
                               <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                                 <input 
                                   type="text" 
                                   placeholder="Subcategory Name" 
                                   value={newSubcatName} 
                                   onChange={(e) => setNewSubcatName(e.target.value)} 
                                   style={{ flex: 1, padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }}
                                 />
                                 <button 
                                   onClick={() => handleAddSubcategory(cat.id)}
                                   style={{ background: '#10b981', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}
                                 >
                                   Add
                                 </button>
                               </div>
                             )}

                             <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                               {(cat.children || []).map(sub => (
                                 <span key={sub.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '16px', padding: '4px 10px', fontSize: '11px', color: '#475569' }}>
                                   {sub.name}
                                   <button 
                                     onClick={() => handleDeleteSubcategory(cat.id, sub.id, sub.name)}
                                     style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, fontSize: '10px', fontWeight: 'bold' }}
                                   >
                                     &times;
                                   </button>
                                 </span>
                               ))}
                               {(cat.children || []).length === 0 && (
                                 <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>No subcategories yet.</span>
                               )}
                             </div>
                           </div>
                         </div>
                       ))}
                     </div>
                   </div>
                 )}

                 {/* Tab 3: Bulk Importer Panel */}
                 {inventorySubTab === 'bulk' && (
                   <div className="admin-add-product-card" style={{
                     background: '#fff',
                     border: '1.5px solid #e2e8f0',
                     borderRadius: '12px',
                     padding: '24px',
                     boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
                   }}>
                     <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                       <DynamicIcon name="fileText" size={18} /> Bulk B2B Excel &amp; CSV Importer
                     </h4>
                     
                     <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                       <div>
                         <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Import Operation Mode</label>
                         <select 
                           value={importMode} 
                           onChange={(e: any) => { setImportMode(e.target.value); setImportPreview([]); setImportSummary(null); }}
                           style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#fff', fontWeight: '600', color: '#334155' }}
                         >
                           <option value="standard">Standard Product Import (Add &amp; Update)</option>
                           <option value="stock_update">Quick Stock/Price Update Only</option>
                         </select>
                       </div>
                       {importMode === 'standard' ? (
                         <div>
                           <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>On Duplicate SKU/Name Conflict</label>
                           <select 
                             value={conflictMode} 
                             onChange={(e: any) => setConflictMode(e.target.value)}
                             style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#fff', fontWeight: '600', color: '#334155' }}
                           >
                             <option value="all">Update all details (overwrite fields)</option>
                             <option value="price">Update pricing &amp; discounts only</option>
                             <option value="stock">Update stock quantity only</option>
                             <option value="skip">Skip duplicate products (keep original)</option>
                           </select>
                         </div>
                       ) : (
                         <div style={{ display: 'flex', alignItems: 'center', color: '#0369a1', fontSize: '12px', lineHeight: '1.4', fontWeight: '500', background: '#e0f2fe', padding: '10px 14px', borderRadius: '6px' }}>
                           <div>
                             <strong>Quick Stock Mode:</strong> Matches products by SKU and updates stock/price. Unmatched SKUs are skipped and reported in the logs.
                           </div>
                         </div>
                       )}
                     </div>

                     <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                       <button 
                         onClick={downloadProductTemplate}
                         style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                       >
                         <DynamicIcon name="receipt" size={14} /> Download Product Template (.csv)
                       </button>
                       <button 
                         onClick={downloadStockTemplate}
                         style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                       >
                         <DynamicIcon name="receipt" size={14} /> Download Stock Update Template (.csv)
                       </button>
                     </div>

                     <div style={{
                       border: '2px dashed #cbd5e1',
                       borderRadius: '8px',
                       padding: '30px 20px',
                       textAlign: 'center',
                       background: '#f8fafc',
                       cursor: 'pointer',
                       marginBottom: '20px',
                       position: 'relative'
                     }}>
                       <input 
                         type="file" 
                         accept=".xlsx,.xls,.csv" 
                         onChange={handleFileUpload}
                         style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                       />
                       <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                         <DynamicIcon name="fileText" size={32} color="#94a3b8" />
                         <span style={{ fontSize: '14px', color: '#475569', fontWeight: '600' }}>Click to upload or drag &amp; drop file here</span>
                         <span style={{ fontSize: '11px', color: '#94a3b8' }}>Supported formats: Excel (.xlsx, .xls), CSV (.csv)</span>
                       </div>
                     </div>

                     {importLogs.length > 0 && (
                       <div style={{ background: '#0f172a', color: '#38bdf8', fontFamily: 'monospace', padding: '12px 16px', borderRadius: '8px', fontSize: '12px', maxHeight: '120px', overflowY: 'auto', marginBottom: '20px', lineHeight: '1.6' }}>
                         {importLogs.map((log, lIdx) => (
                           <div key={lIdx}>&gt; {log}</div>
                         ))}
                       </div>
                     )}

                     {importSummary && (
                       <div style={{ background: '#ecfdf5', border: '1.5px solid #34d399', borderRadius: '8px', padding: '16px', marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', textAlign: 'center' }}>
                         <div>
                           <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#065f46', fontWeight: '700' }}>Added</div>
                           <div style={{ fontSize: '20px', fontWeight: '800', color: '#047857' }}>{importSummary.added}</div>
                         </div>
                         <div>
                           <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#065f46', fontWeight: '700' }}>Updated</div>
                           <div style={{ fontSize: '20px', fontWeight: '800', color: '#047857' }}>{importSummary.updated}</div>
                         </div>
                         <div>
                           <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#b45309', fontWeight: '700' }}>Skipped</div>
                           <div style={{ fontSize: '20px', fontWeight: '800', color: '#b45309' }}>{importSummary.skipped}</div>
                         </div>
                         <div>
                           <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#991b1b', fontWeight: '700' }}>Failed</div>
                           <div style={{ fontSize: '20px', fontWeight: '800', color: '#b91c1c' }}>{importSummary.failed}</div>
                         </div>
                       </div>
                     )}

                     {importPreview.length > 0 && (
                       <div style={{ marginBottom: '20px' }}>
                         <h5 style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>Pre-Import Validation Preview</h5>
                         <div style={{ overflowX: 'auto', maxHeight: '200px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                           <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                             <thead>
                               <tr style={{ background: '#f1f5f9' }}>
                                 <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1' }}>Row</th>
                                 <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1' }}>SKU</th>
                                 <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1' }}>Product Name</th>
                                 <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1' }}>Category</th>
                                 <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1' }}>Price</th>
                                 <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1' }}>Stock</th>
                                 <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1' }}>Status</th>
                               </tr>
                             </thead>
                             <tbody>
                               {importPreview.map((item, pIdx) => (
                                 <tr key={pIdx} style={{ borderBottom: '1px solid #e2e8f0', background: item.status === 'invalid' ? '#fef2f2' : (item.status === 'exists' ? '#fffbeb' : '#ffffff') }}>
                                   <td style={{ padding: '8px 12px', fontWeight: '600' }}>{item.rowIdx}</td>
                                   <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{item.sku}</td>
                                   <td style={{ padding: '8px 12px' }}>{item.name}</td>
                                   <td style={{ padding: '8px 12px' }}>{item.category}</td>
                                   <td style={{ padding: '8px 12px' }}>{item.price}</td>
                                   <td style={{ padding: '8px 12px' }}>{item.stock}</td>
                                   <td style={{ padding: '8px 12px', fontWeight: '700', color: item.status === 'invalid' ? '#ef4444' : (item.status === 'exists' ? '#b45309' : '#10b981') }}>
                                     {item.status.toUpperCase()}
                                   </td>
                                 </tr>
                               ))}
                             </tbody>
                           </table>
                         </div>
                       </div>
                     )}

                     <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                       <button 
                         onClick={() => { setInventorySubTab('products'); setImportPreview([]); setImportSummary(null); setImportLogs([]); }} 
                         style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                       >
                         Close Importer
                       </button>
                       <button 
                         onClick={handleExecuteImport} 
                         disabled={importPreview.length === 0}
                         style={{
                           background: importPreview.length === 0 ? '#94a3b8' : '#10b981',
                           color: '#fff',
                           border: 'none',
                           padding: '8px 20px',
                           borderRadius: '6px',
                           cursor: importPreview.length === 0 ? 'not-allowed' : 'pointer',
                           fontWeight: '600'
                         }}
                       >
                         Execute Import
                       </button>
                     </div>
                   </div>
                 )}
               </div>
             );
           })()}

          {activeTab === 'orders' && (
             <div className="admin-editor">
               <h3>Orders & Payments</h3>
               <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>Track customer orders, payment status, and fulfillment in real-time.</p>
               <div className="admin-table-container">
                 <table className="admin-table">
                   <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Payment Status</th>
                        <th>Fulfillment</th>
                        <th>Actions</th>
                      </tr>
                   </thead>
                   <tbody>
                      {adminOrders.map((o: any) => (
                        <tr key={o.orderId}>
                          <td style={{ fontWeight: '700' }}>{o.orderId}</td>
                          <td>
                            <div style={{ fontWeight: '600' }}>{o.shippingDetails?.firstName} {o.shippingDetails?.lastName}</div>
                            <div style={{ fontSize: '11px', color: '#666' }}>{o.userEmail}</div>
                          </td>
                          <td>{o.date}</td>
                          <td style={{ fontWeight: '750', color: '#df2027' }}>₹{parseFloat(o.total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td><span style={{ color: 'green', fontWeight: 'bold' }}>Paid</span></td>
                          <td>
                            <select 
                              value={o.status} 
                              onChange={(e) => handleFulfillmentChange(o.orderId, e.target.value)}
                              style={{ padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontWeight: '600' }}
                            >
                              <option value="Processing">Processing</option>
                              <option value="Shipped">Shipped</option>
                              <option value="Delivered">Delivered</option>
                            </select>
                          </td>
                          <td>
                            <button 
                              className="admin-btn-save" 
                              onClick={() => dispatch(addNotification({ type: 'success', title: 'Synced', message: `Fulfillment settings for order ${o.orderId} synced store-wide!` }))}
                            >
                              Sync
                            </button>
                          </td>
                        </tr>
                      ))}
                      {adminOrders.length === 0 && (
                        <tr>
                          <td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: '20px' }}>No orders placed yet.</td>
                        </tr>
                      )}
                   </tbody>
                 </table>
               </div>
             </div>
          )}

          {activeTab === 'customers' && (
             <div className="admin-editor">
                <h3>Customer Management</h3>
                <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>View registered B2B buyers and manage their accounts in real-time.</p>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                       <tr>
                         <th>Customer ID</th>
                         <th>Name</th>
                         <th>Email</th>
                         <th>Company / Mobile</th>
                         <th>Registered Date</th>
                         <th>Status</th>
                         <th>Actions</th>
                       </tr>
                    </thead>
                    <tbody>
                       {adminCustomers.map((c: any, index: number) => (
                         <tr key={c.email}>
                           <td style={{ fontWeight: '600' }}>{c.id || `CUST_${1001 + index}`}</td>
                           <td style={{ fontWeight: '600' }}>{c.firstName} {c.lastName}</td>
                           <td>{c.email}</td>
                           <td>
                             <div style={{ fontWeight: '500' }}><div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><DynamicIcon name="bank" size={12} /> {c.companyName || 'Apex B2B Solutions'}</div></div>
                             <div style={{ fontSize: '11px', color: '#666' }}><div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><DynamicIcon name="phone" size={11} /> {c.phone || '+91 9667722651'}</div></div>
                           </td>
                           <td>{c.createdAt || '17/05/2026'}</td>
                           <td>
                             <span style={{ 
                               color: suspendedUsers.includes(c.email) ? '#ef4444' : '#10b981', 
                               fontWeight: 'bold',
                               background: suspendedUsers.includes(c.email) ? '#fef2f2' : '#f0fdf4',
                               padding: '3px 8px',
                               borderRadius: '4px',
                               border: '1px solid currentColor',
                               fontSize: '11px'
                             }}>
                               {suspendedUsers.includes(c.email) ? 'Suspended' : 'Active'}
                             </span>
                           </td>
                           <td>
                             <button 
                               className="admin-btn-delete" 
                               style={{ background: suspendedUsers.includes(c.email) ? '#10b981' : '#f0ad4e', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                               onClick={() => handleToggleSuspend(c.email)}
                             >
                               {suspendedUsers.includes(c.email) ? 'Restore' : 'Suspend'}
                             </button>
                           </td>
                         </tr>
                       ))}
                       {adminCustomers.length === 0 && (
                         <tr>
                           <td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: '20px' }}>No customers registered yet.</td>
                         </tr>
                       )}
                    </tbody>
                  </table>
                </div>
             </div>
          )}
          
          {activeTab === 'settings' && (
            <div className="admin-editor">
               <h3>Global Settings</h3>
               <p>Configure store-wide settings and maintenance mode.</p>
               <div className="admin-form-group">
                 <label>Store Name</label>
                 <input type="text" defaultValue="Moglix B2B E-commerce" />
               </div>
                <div className="admin-form-group">
                 <label>Maintenance Mode</label>
                 <select 
                   value={localMaintenance ? 'Enabled' : 'Disabled'}
                   onChange={(e) => setLocalMaintenance(e.target.value === 'Enabled')}
                 >
                   <option value="Disabled">Disabled</option>
                   <option value="Enabled">Enabled</option>
                 </select>
               </div>
               <button className="admin-btn-save" onClick={handleSaveSettings}>Save Settings</button>
            </div>
          )}

          {activeTab === 'rfqs' && (
            <div className="admin-editor">
              <h3>B2B RFQ wholesale Quote Submissions</h3>
              <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>Manage wholesale queries, review customer price bids, and approve special B2B commercial rates.</p>
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>RFQ ID</th>
                      <th>Product Name / Category</th>
                      <th>Quantity</th>
                      <th>Target Bid</th>
                      <th>Special Price Offered</th>
                      <th>Date / Expected</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminRFQs.map((r: any) => (
                      <tr key={r.id}>
                        <td style={{ fontWeight: '700' }}>{r.id}</td>
                        <td style={{ fontWeight: '600' }}>
                          <div>{r.productName}</div>
                          <div style={{ fontSize: '11px', color: '#666' }}>Buyer: {r.userName || r.userEmail} ({r.userEmail})</div>
                        </td>
                        <td style={{ fontWeight: '600' }}>{r.quantity} Pcs</td>
                        <td style={{ color: '#475569', fontWeight: 'bold' }}>₹{r.targetPrice || 2000}/pc</td>
                        <td style={{ color: '#10b981', fontWeight: 'bold' }}>
                          {r.specialPrice ? `₹${r.specialPrice}/pc` : 'None Offered'}
                        </td>
                        <td>
                          <div style={{ fontSize: '12px' }}><div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><DynamicIcon name="calendar" size={12} /> {r.date}</div></div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}><div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><DynamicIcon name="box" size={11} /> Expect by: {r.expectedDate || 'ASAP'}</div></div>
                        </td>
                        <td>
                          <span style={{
                            color: r.status.includes('Approved') || r.status.includes('Offered') ? '#10b981' : '#f59e0b',
                            background: r.status.includes('Approved') || r.status.includes('Offered') ? '#e6f4ea' : '#fef3c7',
                            fontWeight: 'bold',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            border: '1px solid currentColor',
                            fontSize: '11px'
                          }}>
                            {r.status}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              style={{ background: '#10b981', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '11px' }}
                              onClick={() => handleRFQAction(r.id, 'approve')}
                            >
                              Offer Special Discount
                            </button>
                            <button
                              style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '11px' }}
                              onClick={() => handleRFQAction(r.id, 'negotiate')}
                            >
                              Negotiate Remarks
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {adminRFQs.length === 0 && (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: '20px' }}>No RFQs raised yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── HOW TO ADD PRODUCTS GUIDE ── */}
          {activeTab === 'how_to_add' && (
            <div className="admin-editor" style={{ maxWidth: '860px' }}>
              <h3 style={{ fontSize: '22px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="bookOpen" size={22} /> How to Add Products</h3>
              <p style={{ color: '#64748b', marginBottom: '28px', fontSize: '14px' }}>
                A step-by-step guide for adding new products to the store — no coding experience required for the easy method.
              </p>

              {/* Method 1 */}
              <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ background: '#16a34a', color: '#fff', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '16px', flexShrink: 0 }}>1</span>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '17px', color: '#15803d', display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="checkCircle" size={18} stroke="#15803d" /> Easy Method — Add via Admin Panel (Recommended)</h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#166534' }}>Best for non-technical users. No code editing required.</p>
                  </div>
                </div>
                <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '2', color: '#166534', fontSize: '14px' }}>
                  <li>Click <strong>"Inventory &amp; Products"</strong> in the left sidebar.</li>
                  <li>Scroll to the <strong>"Add New Product"</strong> form at the bottom of the page.</li>
                  <li>Fill in all the required fields (see field guide below).</li>
                  <li>Click <strong>"Add Product"</strong> to save it.</li>
                  <li>The product will appear instantly in the store's <strong>Shop page</strong> and under the relevant category.</li>
                </ol>
                <div style={{ background: '#dcfce7', borderRadius: '8px', padding: '12px 16px', marginTop: '14px', fontSize: '13px', color: '#166534' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '4px' }}><DynamicIcon name="info" size={14} stroke="#166534" /></span> <strong>Permanent Database Persistence:</strong> Products added via this form are sent directly to the central backend REST API and saved in the database server. They are instantly live and permanent across all devices, users, and browsers.
                </div>
              </div>

              {/* Method 2 */}
              <div style={{ background: '#eff6ff', border: '1.5px solid #93c5fd', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ background: '#2563eb', color: '#fff', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '16px', flexShrink: 0 }}>2</span>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '17px', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="cabinet" size={18} stroke="#1d4ed8" /> Permanent Method — Edit <code style={{ background: '#dbeafe', padding: '1px 6px', borderRadius: '4px' }}>src/data/db.ts</code></h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#1e40af' }}>For developers only. Products added here are permanent and appear on all devices.</p>
                  </div>
                </div>
                <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '2', color: '#1e40af', fontSize: '14px' }}>
                  <li>Open the file <code style={{ background: '#dbeafe', padding: '1px 6px', borderRadius: '4px' }}>react-ecommerce/src/data/db.ts</code> in your code editor.</li>
                  <li>Scroll to the end of the <code>dbProducts.products</code> array (just before the closing <code>{`]`}</code>).</li>
                  <li>Add a comma after the last product entry, then add your new product object using the template below.</li>
                  <li>Save the file. The product will appear immediately after the page refreshes.</li>
                </ol>
                <div style={{ background: '#dbeafe', borderRadius: '8px', padding: '14px 16px', marginTop: '14px', fontFamily: 'monospace', fontSize: '12px', color: '#1e3a8a', whiteSpace: 'pre', overflowX: 'auto', lineHeight: '1.6' }}>
{`{
  "id": "prod_211",          // Use the next number in sequence
  "name": "Product Name",
  "category": "Security Systems",  // Must match a category in categoryStructure.ts
  "subcategory": "IP Cameras",
  "brand": "Hikvision",
  "image": "https://your-image-url.jpg",
  "rating": "4.5",
  "reviewsCount": 100,
  "price": "₹5,000",
  "oldPrice": "₹6,500",
  "discount": "23% OFF",
  "inStock": true,
  "stockQuantity": 50,
  "isDeal": false,
  "tags": ["IP Camera", "4MP"],
  "sku": "YOUR-MODEL-NUMBER",
  "description": "Full product description here."
}`}
                </div>
              </div>

              {/* Method 3: Bulk Product Import */}
              <div style={{ background: '#f5f3ff', border: '1.5px solid #ddd6fe', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ background: '#7c3aed', color: '#fff', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '16px', flexShrink: 0 }}>3</span>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '17px', color: '#6d28d9', display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="fileText" size={18} stroke="#6d28d9" /> Bulk Product Import Method (Excel / CSV)</h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#5b21b6' }}>Best for bulk loading large product catalogs. Automatic category generation and conflict resolution.</p>
                  </div>
                </div>
                <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '2', color: '#5b21b6', fontSize: '14px' }}>
                  <li>Go to the <strong>"Inventory &amp; Products"</strong> tab and click the <strong>"Bulk Excel Import"</strong> button.</li>
                  <li>Click <strong>"Download Product Template (.csv)"</strong> to save the standard data format.</li>
                  <li>Fill in the fields (Product Name, SKU, Category, Brand, Price, etc.) using Microsoft Excel, Google Sheets, or standard CSV editor.</li>
                  <li>Select your preferred duplicate handling mode (e.g. overwrite all, update pricing only, update stock only, or skip).</li>
                  <li>Upload the file. The validation grid will preview data and highlight any structural warnings or missing fields.</li>
                  <li>Click <strong>"Execute Import"</strong> to commit the catalog additions to the live database in real-time.</li>
                </ol>
              </div>

              {/* Method 4: Bulk Stock Update */}
              <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ background: '#ea580c', color: '#fff', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '16px', flexShrink: 0 }}>4</span>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '17px', color: '#c2410c', display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="box" size={18} stroke="#c2410c" /> Quick Stock/Price Update</h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#9a3412' }}>Fastest method to update inventory levels and wholesale pricing across existing catalog.</p>
                  </div>
                </div>
                <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '2', color: '#9a3412', fontSize: '14px' }}>
                  <li>Open the bulk importer and change the operation mode to <strong>"Quick Stock/Price Update Only"</strong>.</li>
                  <li>Download the dedicated <strong>Stock Update Template</strong>.</li>
                  <li>Populate the sheets with the matched <strong>SKU</strong>, <strong>Stock Quantity</strong>, and <strong>Price</strong>.</li>
                  <li>Upload the sheet and click <strong>"Execute Import"</strong>. Unmatched SKUs are skipped and reported in the importer logs.</li>
                </ol>
              </div>

              {/* Field Reference */}
              <h4 style={{ fontSize: '16px', marginBottom: '12px', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="fileText" size={18} /> Field Reference Guide</h4>
              <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', color: '#475569' }}>Field</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', color: '#475569' }}>Required</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', color: '#475569' }}>Description &amp; Examples</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { field: 'id', req: 'Yes', desc: 'Unique ID. Use "prod_" + next number. E.g., "prod_211"' },
                      { field: 'name', req: 'Yes', desc: 'Full product name including model number. E.g., "CP Plus 4MP IP Dome Camera | CP-UNC-DA41L3C-LQ"' },
                      { field: 'category', req: 'Yes', desc: 'Must match exactly: "Security Systems", "IT & Networking", "Communication Systems", "Hardware", "Lighting Solutions"' },
                      { field: 'subcategory', req: 'Optional', desc: 'E.g., "IP Cameras", "CCTV Accessories", "Network Switches", "Biometric Systems"' },
                      { field: 'brand', req: 'Yes', desc: 'Brand name. E.g., "Hikvision", "CP Plus", "Dahua", "Honeywell"' },
                      { field: 'image', req: 'Yes', desc: 'Full URL to product image. Use the manufacturer site, Wix CDN, or any public URL.' },
                      { field: 'rating', req: 'Yes', desc: 'A string like "4.5". Use values between 3.5 and 5.0.' },
                      { field: 'reviewsCount', req: 'Yes', desc: 'Number of reviews (integer). E.g., 142' },
                      { field: 'price', req: 'Yes', desc: 'Selling price with ₹ symbol. E.g., "₹5,300" or "₹1,450"' },
                      { field: 'oldPrice', req: 'Optional', desc: 'Original/MRP price for showing strikethrough. E.g., "₹7,000"' },
                      { field: 'discount', req: 'Optional', desc: 'Discount label. E.g., "24% OFF"' },
                      { field: 'inStock', req: 'Yes', desc: 'true or false. Controls "Add to Cart" vs "Out of Stock" button.' },
                      { field: 'stockQuantity', req: 'Yes', desc: 'Number of units available. Set to 0 if inStock is false.' },
                      { field: 'isDeal', req: 'Yes', desc: 'true = shows product in "Deals" section. false = regular product.' },
                      { field: 'tags', req: 'Optional', desc: 'Array of strings for filtering. E.g., ["IP Camera", "4MP", "Night Color"]' },
                      { field: 'sku', req: 'Optional', desc: 'Manufacturer model number. E.g., "CP-UNC-DA41L3C-LQ"' },
                      { field: 'description', req: 'Yes', desc: 'Full product description. Write 2–4 sentences covering key features.' },
                    ].map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                        <td style={{ padding: '9px 14px', fontFamily: 'monospace', color: '#0f172a', fontWeight: '600' }}>{row.field}</td>
                        <td style={{ padding: '9px 14px', color: row.req === 'Yes' ? '#16a34a' : '#64748b', fontWeight: '600' }}>{row.req === 'Yes' ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#16a34a' }}><DynamicIcon name="check" size={12} stroke="#16a34a" /> Yes</span> : <span style={{ color: '#64748b' }}>Optional</span>}</td>
                        <td style={{ padding: '9px 14px', color: '#475569' }}>{row.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Valid Categories */}
              <h4 style={{ fontSize: '16px', marginBottom: '12px', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="folder" size={18} /> Valid Categories &amp; Subcategories</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '24px' }}>
                {[
                  { cat: 'Security Systems', subs: ['IP Cameras', 'CCTV Cameras', 'CCTV Accessories', 'Biometric Systems', 'Access Control', 'Video Door Phones', 'Metal Detectors'] },
                  { cat: 'IT & Networking', subs: ['Network Switches', 'WiFi Routers & Access Points', 'Cables & Connectors', 'Media Converters'] },
                  { cat: 'Communication Systems', subs: ['EPABX Systems', 'Intercom Systems', 'Conference Phones', 'Landline Phones'] },
                  { cat: 'Hardware', subs: ['Fasteners', 'Hand Tools', 'Power Tools', 'Storage Solutions'] },
                ].map((item, i) => (
                  <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px' }}>
                    <div style={{ fontWeight: '700', color: '#0f172a', marginBottom: '8px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}><DynamicIcon name="folder" size={14} /> {item.cat}</div>
                    {item.subs.map((s, j) => (
                      <div key={j} style={{ fontSize: '12px', color: '#475569', padding: '2px 0 2px 12px', borderLeft: '2px solid #e2e8f0' }}>└ {s}</div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Bulk Importer & Stock Management Documentation */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '28px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Import Architecture &amp; Execution Rules</h4>
                <div style={{ fontSize: '14px', lineHeight: '1.7', color: '#475569' }}>
                  <p style={{ marginBottom: '12px' }}>
                    <strong>1. File Formats:</strong> The bulk system supports <code>.xlsx</code>, <code>.xls</code>, and <code>.csv</code>. If you are uploading an Excel sheet, the first worksheet (Sheet 1) will be parsed.
                  </p>
                  <p style={{ marginBottom: '12px' }}>
                    <strong>2. Automated Category Management:</strong> During import, the category and subcategory text are matched. If a category is not present in the catalog, it is automatically generated and added. New subcategories are instantly grouped under their respective parent categories.
                  </p>
                  <p style={{ marginBottom: '12px' }}>
                    <strong>3. Conflict Resolution &amp; Duplicate Prevention:</strong> Match is executed by SKU (or Product Name if SKU is blank). You can customize behavior using four conflict modes:
                    <ul style={{ paddingLeft: '20px', marginTop: '6px' }}>
                      <li><strong>Update all fields:</strong> Overwrites description, image URL, category, brand, and pricing.</li>
                      <li><strong>Update pricing only:</strong> Overwrites only the Selling Price and discount details.</li>
                      <li><strong>Update stock only:</strong> Updates only stock quantity and changes stock availability badges.</li>
                      <li><strong>Skip duplicate products:</strong> Skips any row that matches an existing SKU to prevent unwanted overwrites.</li>
                    </ul>
                  </p>
                  <p style={{ marginBottom: '12px' }}>
                    <strong>4. Bulk Image Handling &amp; Local Storage:</strong> If image URLs are supplied in the import columns, they will be loaded immediately. If a product row does not include an image, the importer assigns an illustration-specific category placeholder (e.g. Dome Camera, Router, EPABX) based on category tags.
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>5. Image Asset Localization Script:</strong> Images referenced by external URLs can be downloaded to your local public folder by running the Python utility <code>python3 scratch/download_new_excel_images.py</code> from the terminal. This copies all external URLs to <code>public/assets/images/nicedeal/</code> and redirects product paths.
                  </p>
                </div>
              </div>

              {/* Tips */}
              <h4 style={{ fontSize: '16px', marginBottom: '12px', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="info" size={18} /> Tips &amp; Troubleshooting</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                {[
                  { icon: 'image', title: 'Product Image Not Loading?', text: 'Make sure the image URL is publicly accessible (not behind a login). You can upload images to Imgur, Cloudinary, or Google Drive (set to public) and use that link.' },
                  { icon: 'cabinet', title: 'Are Added Products Permanent?', text: 'Yes! All products added via the Admin Panel form are stored in the central backend database and remain permanent across all devices and page refreshes.' },
                  { icon: 'pin', title: 'Duplicate ID Error?', text: 'Every product must have a unique "id". Check the last product in db.ts and increment the number. E.g., if last is "prod_210", use "prod_211".' },
                  { icon: 'folder', title: 'Product Not Showing in Category?', text: 'The "category" field must exactly match a category name in categoryStructure.ts. Check capitalization. "Security Systems" works, "security systems" does not.' },
                  { icon: 'lightning', title: 'Price Format?', text: 'Always include the ₹ symbol and use comma formatting for thousands. E.g., "₹1,450" not "1450" or "Rs. 1450".' },
                ].map((tip, i) => (
                  <div key={i} style={{ display: 'flex', gap: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px' }}>
                    <span style={{ flexShrink: 0, color: '#3b82f6', display: 'flex', alignItems: 'center' }}><DynamicIcon name={tip.icon} size={20} /></span>
                    <div>
                      <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '13px', marginBottom: '4px' }}>{tip.title}</div>
                      <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>{tip.text}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px', padding: '14px 18px', marginTop: '8px', fontSize: '13px', color: '#854d0e' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><DynamicIcon name="alertTriangle" size={16} stroke="#854d0e" /> <span><strong>Important:</strong> After editing <code>db.ts</code>, you must redeploy the app (run <code>npm run build</code> and upload the new build) for changes to be visible to all users.</span></div>
              </div>
            </div>
          )}

        </section>
      </main>
    </div>
  );
};

export default AdminPanel;
